import React from 'react';

import {
  Tabs,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { ComponentLoader, messageError, PageSection } from '@deltaohm/ant-components';
import { useVibrationSession } from './hooks';
import { extractGraphQLErrors } from '../utils/graphql';
import { ErrorList } from '../generics';

import {
  VibrationSessionPropertyTab,
  VibrationSessionMeasuresTab,
  VibrationSessionSetupTab,
} from './tabs';

const { TabPane } = Tabs;

const VibrationSessionDetailPage = () => {
  const { workspaceId, vibrationSessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const {
    error,
    loading,
    workspace,
    vibrationSession,
  } = useVibrationSession(workspaceId, vibrationSessionId);

  const breadcrumb = React.useMemo(() => {
    const result = [
      {
        path: '/workspaces',
        breadcrumbName: 'Workspaces',
      },
    ];
    if (workspace) {
      result.push({
        path: `/workspaces/${workspaceId}`,
        breadcrumbName: workspace.name,
      });
      if (vibrationSession) {
        result.push({
          path: `/workspaces/${workspaceId}/vibrationSessions/${vibrationSessionId}}`,
          breadcrumbName: vibrationSession.name,
        });
      }
    }
    return result;
  }, [workspace, vibrationSession]);

  React.useEffect(() => {
    if (error) {
      const errors = extractGraphQLErrors(error, t);
      messageError({
        content: <ErrorList errors={errors} />,
      });
    }
  }, [error, t]);

  const activeTab = React.useMemo(() => {
    const tab = searchParams.get('tab');
    if (!tab) {
      return 'setup';
    }
    return tab;
  }, [searchParams]);

  const handleChangeTab = React.useCallback((newTab) => {
    setSearchParams({ tab: newTab });
  }, [setSearchParams]);

  return (
    <ComponentLoader
      message={t('common.loading')}
      loading={loading}
    >
      <PageSection
        title={vibrationSession ? vibrationSession.name : ''}
        breadcrumb={breadcrumb}
      >
        <Tabs activeKey={activeTab} onChange={handleChangeTab}>
          <TabPane tab={t('vibrationSessions.detail.tabs.setup')} key="setup">
            {vibrationSession && (
              <VibrationSessionSetupTab
                vibrationSession={vibrationSession}
              />
            )}
          </TabPane>
          <TabPane tab={t('vibrationSessions.detail.tabs.measures')} key="measures">
            {vibrationSession && (
              <VibrationSessionMeasuresTab
                workspaceId={workspaceId}
                vibrationSession={vibrationSession}
              />
            )}
          </TabPane>
          <TabPane tab={t('vibrationSessions.detail.tabs.properties')} key="properties">
            {vibrationSession && (
              <VibrationSessionPropertyTab
                workspaceId={workspaceId}
                vibrationSession={vibrationSession}
              />
            )}
          </TabPane>
        </Tabs>
      </PageSection>
    </ComponentLoader>
  );
};

export default VibrationSessionDetailPage;
